import QuestionCommunityView, { AskYourGroup } from './QuestionCommunityView'
import PollDesign, { pollHeroActionSx } from '@components/common/PollDesign'
import ViewNavigation from '@components/common/ViewNavigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import QuestionAnswerRoundedIcon from '@mui/icons-material/QuestionAnswerRounded'
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded'
import ThumbUpRoundedIcon from '@mui/icons-material/ThumbUpRounded'
import ThumbDownRoundedIcon from '@mui/icons-material/ThumbDownRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import {
  GlassButton,
  GlassCard,
  GlassOutlineButton,
} from '@components/common/Glass'
import PollCheckDecoration from '@components/common/PollCheckDecoration'
import Alert, { AlertInput } from '@components/common/Alert'
import { ShareButton } from '@components/common/ShareButton'
import { Poll } from 'src/api/types/polls'
import { APIResult } from 'src/utils/types'
import {
  closePollAnswers,
  deletePoll,
  getPoll,
  getPollCategories,
  getPollTags,
  hidePollAnswer,
  ratePoll,
  reportPoll,
  submitPollAnswer,
  updatePoll,
} from 'src/api/polls'
import { helperTextColor } from 'src/ui/theme/tokens'

type Props = {
  poll: Poll
  userId?: number
  onUpdated: (poll: Poll) => void
  onBack: () => void
  answerFocus?: boolean
  onAnswerCancel?: () => void
  backLabel?: string
  onPlay: (poll: Poll) => void
  onDeleted: (id: number) => void
  onCopied?: (poll: Poll) => void
  onOpenHowToPlay: () => void
  sounds: { click: () => void; submit: () => void }
}

const splitMeta = (value?: string) =>
  (value || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
const normalizeMeta = (values: string[]) =>
  Array.from(
    new Set(values.map((value) => value.trim().slice(0, 16)).filter(Boolean)),
  ).slice(0, 3)
const disabledPollActionSx = {
  background: '#fff0e0',
  color: '#a77b56',
  borderColor: '#efd7bd',
  boxShadow: '0 2px 0 #efd7bd',
  '& .MuiTypography-root, & .MuiSvgIcon-root': { color: 'inherit' },
}
const answerButtonSx = {
  fontWeight: 700,
  minHeight: 44,
}
const sectionSx = {
  p: { xs: 2, sm: 3 },
  borderColor: '#efddc9',
  boxShadow: '0 3px 0 #e8cdb2',
  textAlign: 'left',
}
const outlinedActionSx = {
  color: '#944217',
  borderColor: '#e8cdb2',
  background: '#fffaf5',
  fontWeight: 700,
  '&:hover': { background: '#fffaf5', borderColor: '#d4a27a' },
  '&.Mui-disabled': disabledPollActionSx,
}

export default function QuestionPollSection({
  poll,
  userId,
  onUpdated,
  onBack,
  answerFocus,
  onAnswerCancel,
  backLabel = 'Polls',
  onPlay,
  onDeleted,
  onCopied,
  onOpenHowToPlay,
  sounds,
}: Props) {
  const owner = !!userId && poll.creatorId === userId
  const closed = !!poll.answersClosedAt
  const living = poll.questionExperienceVersion === 2
  const [editing, setEditing] = useState(
    !poll.answersClosedAt &&
      (answerFocus || !poll.viewerAnswer || poll.viewerAnswer.removed),
  )
  useEffect(() => {
    if (answerFocus && !poll.answersClosedAt) setEditing(true)
  }, [answerFocus, poll.answersClosedAt])
  const [selection, setSelection] = useState<string>(
    poll.viewerAnswer && !poll.viewerAnswer.removed
      ? String(poll.viewerAnswer.itemId)
      : 'new',
  )
  const [answer, setAnswer] = useState(
    poll.viewerAnswer?.removed ? '' : poll.viewerAnswer?.title || '',
  )
  const [displayLabel, setDisplayLabel] = useState(
    poll.viewerAnswer?.displayLabel || '',
  )
  const [busy, setBusy] = useState(false)
  const [resultsOpen, setResultsOpen] = useState(false)
  const [rated, setRated] = useState<0 | 1>()
  const [alert, setAlert] = useState<AlertInput>({ open: false })
  const [confirmation, setConfirmation] = useState<{
    kind: 'close' | 'hide' | 'delete'
    itemId?: number
  }>()
  const [reportOpen, setReportOpen] = useState(false)
  const [reportDetails, setReportDetails] = useState('')
  const [categoryOptions, setCategoryOptions] = useState<string[]>([])
  const [tagOptions, setTagOptions] = useState<string[]>([])
  const [categories, setCategories] = useState(splitMeta(poll.category))
  const [tags, setTags] = useState(splitMeta(poll.tags))
  const [visibility, setVisibility] = useState(
    poll.visibility === 'private' ? 'private' : 'public',
  )
  const [showAnswers, setShowAnswers] = useState(!!poll.showSubmittedAnswers)
  const mounted = useRef(true)
  const order = useRef(new Map<number, number>())
  useEffect(() => {
    if (poll.viewerAnswer?.removed && !closed) {
      setEditing(true)
      setSelection('new')
      setAnswer('')
    }
  }, [poll.viewerAnswer?.removed, closed])
  useEffect(() => {
    mounted.current = true
    window.scrollTo({ top: 0, behavior: 'smooth' })
    let current = true
    if (owner) {
      getPollCategories().then((result) => {
        if (current && result.success) setCategoryOptions(result.data || [])
      })
      getPollTags().then((result) => {
        if (current && result.success) setTagOptions(result.data || [])
      })
    }
    return () => {
      current = false
      mounted.current = false
    }
  }, [owner])

  useEffect(() => {
    if (!living) return
    let current = true
    let pending = false
    const timer = window.setInterval(async () => {
      if (pending || document.visibilityState === 'hidden') return
      pending = true
      try {
        const response = await getPoll(poll.id!)
        if (
          current &&
          response.success &&
          response.data &&
          JSON.stringify(response.data) !== JSON.stringify(poll)
        )
          onUpdated(response.data)
      } finally {
        pending = false
      }
    }, 15000)
    return () => {
      current = false
      window.clearInterval(timer)
    }
  }, [poll, living, onUpdated])

  const choices = useMemo(() => {
    const rows = (poll.items || []).filter((item) => !item.hidden)
    rows.forEach((item) => {
      if (item.id && !order.current.has(item.id))
        order.current.set(item.id, Math.random())
    })
    return rows
      .slice()
      .sort(
        (a, b) =>
          (order.current.get(a.id!) || 0) - (order.current.get(b.id!) || 0),
      )
  }, [poll.items])

  const perform = async (
    request: () => Promise<APIResult<Poll>>,
    message: string,
  ) => {
    if (busy) return
    setBusy(true)
    try {
      const result = await request()
      if (!mounted.current) return
      if (!result.success || !result.data) {
        if (result.message === 'Submissions are closed.') {
          const refreshed = await getPoll(poll.id!)
          if (mounted.current && refreshed.success && refreshed.data)
            onUpdated(refreshed.data)
        }
        throw new Error(result.message || 'Could not update this question.')
      }
      onUpdated(result.data)
      setAlert({ open: true, severity: 'success', message })
      return result.data
    } catch (error) {
      if (mounted.current)
        setAlert({
          open: true,
          severity: 'error',
          message: error instanceof Error ? error.message : 'Please try again.',
        })
    } finally {
      if (mounted.current) setBusy(false)
    }
  }

  const submit = async () => {
    sounds.click()
    const useChoice = poll.showSubmittedAnswers && selection !== 'new'
    const next = await perform(
      () =>
        submitPollAnswer({
          pollId: poll.id!,
          ...(living ? { displayLabel } : {}),
          ...(useChoice
            ? { answerItemId: Number(selection) }
            : { answerText: answer }),
        }),
      'Answer submitted.',
    )
    if (next) {
      sounds.submit()
      setEditing(false)
    }
  }
  const confirm = async () => {
    sounds.click()
    if (confirmation?.kind === 'delete') {
      setBusy(true)
      try {
        const result = await deletePoll(poll.id!)
        if (!result.success)
          throw new Error(result.message || 'Could not delete this question.')
        onDeleted(poll.id!)
      } catch (error) {
        setAlert({
          open: true,
          severity: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Could not delete this question.',
        })
      } finally {
        if (mounted.current) setBusy(false)
      }
    } else if (confirmation?.kind === 'close') {
      await perform(
        () => closePollAnswers(poll.id!),
        living
          ? 'Submissions closed. Preparing the final ranking.'
          : 'Submissions closed. Results are ready.',
      )
    } else if (confirmation?.itemId) {
      await perform(
        () => hidePollAnswer(poll.id!, confirmation.itemId!),
        'Answer hidden.',
      )
    }
    if (mounted.current) setConfirmation(undefined)
  }
  const forcedPrivate =
    poll.moderationStatus === 'offensive' || poll.moderationStatus === 'blocked'
  const metaChanged =
    categories.join(',') !== (poll.category || '') ||
    tags.join(',') !== (poll.tags || '') ||
    visibility !== poll.visibility ||
    showAnswers !== !!poll.showSubmittedAnswers
  const canSubmit =
    !busy &&
    (!living || Array.from(displayLabel.trim()).length <= 12) &&
    poll.canSubmitAnswer &&
    ((poll.showSubmittedAnswers && selection !== 'new') ||
      (answer.trim().length > 0 && Array.from(answer.trim()).length <= 120))

  return (
    <PollDesign>
      <Stack
        spacing={2}
        pb={2}
        sx={{ minWidth: 0, '& > .rt-view-navigation + *': { mt: '10px' } }}
      >
        <ViewNavigation mb={0}>
          <GlassOutlineButton
            startIcon={<ArrowBackRoundedIcon />}
            onClick={onBack}
          >
            {backLabel}
          </GlassOutlineButton>
          <Chip
            label="How to Play"
            onClick={() => {
              onOpenHowToPlay()
            }}
          />
        </ViewNavigation>
        <GlassCard
          className="rt-question-hero"
          sx={{
            ...sectionSx,
            background: '#ffefdd',
            boxShadow: '0 4px 0 #e8bb99',
            position: 'relative',
            isolation: 'isolate',
            '& > :not(.rt-poll-decoration)': {
              position: 'relative',
              zIndex: 1,
            },
          }}
        >
          <PollCheckDecoration />
          <Stack spacing={2}>
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <Chip
                icon={<QuestionAnswerRoundedIcon />}
                label="Community Answers"
                sx={{
                  background: '#ffdfc2',
                  color: '#693413',
                  '& .MuiChip-icon': { color: '#693413' },
                }}
              />
              <Chip
                size="small"
                label={
                  closed ? 'Submissions Closed' : living ? 'Open' : 'Collecting'
                }
                sx={{ background: '#fffaf5', color: '#855c3d' }}
              />
              {!closed && !poll.showSubmittedAnswers && (
                <Chip
                  size="small"
                  icon={<VisibilityOffRoundedIcon />}
                  label="Answers Hidden"
                  sx={{
                    background: '#fffaf5',
                    color: '#855c3d',
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={{ xs: 1.5, sm: 2 }}>
              <Box
                component="img"
                src="/brand/random-poll.svg"
                alt=""
                aria-hidden="true"
                sx={{ width: { xs: 56, sm: 80 }, flexShrink: 0 }}
              />
              <Box minWidth={0}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: 26, sm: 34 },
                    color: '#693413',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {poll.prompt}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75, color: '#855c3d' }}>
                  {closed
                    ? 'The answers are in. Explore the community’s picks.'
                    : 'One question. Everyone’s own take.'}
                </Typography>
              </Box>
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1.5}
            >
              <Box display="flex" gap={1}>
                {[
                  ['Responses', poll.submissions || 0],
                  ['Plays', poll.plays || 0],
                ].map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{
                      py: 1,
                      px: 1.5,
                      background: '#fffaf5',
                      border: '1px solid #efddc9',
                      borderRadius: '14px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Nunito", sans-serif',
                        fontSize: 20,
                        lineHeight: 1.2,
                        fontWeight: 800,
                        color: '#693413',
                      }}
                    >
                      {value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#855c3d' }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                {living && (
                  <AskYourGroup
                    onCopied={onCopied}
                    poll={poll}
                    userId={userId}
                    sounds={sounds}
                    onUpdated={onUpdated}
                    onPlay={onPlay}
                    onError={(message) =>
                      setAlert({ open: true, severity: 'error', message })
                    }
                  />
                )}
                <ShareButton
                  shareType="poll"
                  pollId={poll.id}
                  listTitle={poll.prompt}
                  onClick={sounds.click}
                  variant="outlined"
                  sx={pollHeroActionSx}
                />
              </Box>
            </Box>
          </Stack>
        </GlassCard>

        <GlassCard className="rt-question-participation" sx={sectionSx}>
          <Stack spacing={2}>
            {poll.viewerAnswer && (
              <Box
                sx={{
                  p: 2,
                  background: '#fffaf5',
                  border: '1px solid #efddc9',
                  borderRadius: '18px',
                }}
              >
                <Box display="flex" gap={1} alignItems="center" mb={0.75}>
                  {poll.viewerAnswer.removed ? (
                    <VisibilityOffRoundedIcon
                      sx={{ color: '#9d4043', fontSize: 20 }}
                    />
                  ) : (
                    <CheckCircleRoundedIcon
                      sx={{ color: '#944217', fontSize: 20 }}
                    />
                  )}
                  <Typography variant="subtitle2" sx={{ color: '#855c3d' }}>
                    Your answer
                  </Typography>
                </Box>
                <Typography
                  fontWeight={700}
                  sx={{ overflowWrap: 'anywhere', color: '#693413' }}
                >
                  {poll.viewerAnswer.title}
                </Typography>
                <Typography
                  variant="body2"
                  color={poll.viewerAnswer.removed ? 'error' : '#855c3d'}
                  sx={{ mt: 0.5 }}
                >
                  {poll.viewerAnswer.removed
                    ? closed
                      ? 'Your answer was removed and is excluded from results.'
                      : 'Your answer was removed. Please submit another answer.'
                    : closed
                      ? 'Submissions are closed.'
                      : 'Answer submitted. You can update it until submissions close.'}
                </Typography>
                {!closed && !editing && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<EditRoundedIcon />}
                    sx={{ ...outlinedActionSx, mt: 1.5 }}
                    disabled={busy}
                    onClick={() => {
                      sounds.click()
                      setAnswer(poll.viewerAnswer!.title)
                      setSelection(String(poll.viewerAnswer!.itemId))
                      setEditing(true)
                    }}
                  >
                    Update Answer
                  </Button>
                )}
              </Box>
            )}

            {!closed && editing && (
              <Box
                component="form"
                onSubmit={(event) => {
                  event.preventDefault()
                  if (canSubmit) void submit()
                }}
              >
                <Stack spacing={2}>
                  <Typography
                    variant="h3"
                    sx={{ fontSize: 24, color: '#693413' }}
                  >
                    {poll.viewerAnswer
                      ? 'Change your answer'
                      : 'Add your answer'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#855c3d' }}>
                    {poll.showSubmittedAnswers
                      ? 'Choose an answer or add your own.'
                      : 'Submit your answer. Other submissions stay hidden until results open.'}
                  </Typography>
                  {poll.showSubmittedAnswers && choices.length > 0 && (
                    <RadioGroup
                      sx={{ gap: 1 }}
                      value={selection}
                      onChange={(_event, value) => {
                        sounds.click()
                        setSelection(value)
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          maxHeight: living ? 320 : undefined,
                          overflowY: living ? 'auto' : undefined,
                        }}
                      >
                        {choices.map((item) => (
                          <FormControlLabel
                            key={item.id}
                            value={String(item.id)}
                            disabled={busy}
                            control={<Radio color="secondary" />}
                            label={item.title}
                            sx={{
                              m: 0,
                              p: 0.75,
                              border: `1px solid ${selection === String(item.id) ? '#e9a573' : '#efddc9'}`,
                              borderRadius: '14px',
                              background:
                                selection === String(item.id)
                                  ? '#ffefdd'
                                  : '#fffaf5',
                              transition:
                                'background-color 180ms ease, border-color 180ms ease',
                              '& .MuiFormControlLabel-label': {
                                color: '#693413',
                                fontWeight: 600,
                                overflowWrap: 'anywhere',
                                minWidth: 0,
                              },
                            }}
                          />
                        ))}
                      </Box>
                      <FormControlLabel
                        value="new"
                        disabled={busy || (!living && !poll.canAddAnswer)}
                        control={<Radio color="secondary" />}
                        label="Something else…"
                        sx={{
                          m: 0,
                          p: 0.75,
                          borderRadius: '14px',
                          border:
                            selection === 'new'
                              ? '1px solid #e9a573'
                              : '1px dashed #e8cdb2',
                          background:
                            selection === 'new' ? '#ffdfc2' : '#fffaf5',
                          boxShadow:
                            selection === 'new'
                              ? 'inset 0 0 0 1px #e9a573'
                              : 'none',
                          color: '#944217',
                          transition:
                            'background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
                          '& .MuiFormControlLabel-label': {
                            fontWeight: selection === 'new' ? 700 : 600,
                          },
                          '& .MuiRadio-root.Mui-checked': { color: '#944217' },
                        }}
                      />
                    </RadioGroup>
                  )}
                  {(!poll.showSubmittedAnswers || selection === 'new') && (
                    <TextField
                      fullWidth
                      label="Your answer"
                      placeholder="Share your take…"
                      value={answer}
                      disabled={
                        busy ||
                        (!living && !poll.canAddAnswer && !poll.viewerAnswer)
                      }
                      onChange={(event) => setAnswer(event.target.value)}
                      inputProps={{ maxLength: 240 }}
                      helperText={`${Array.from(answer).length}/120 characters`}
                      sx={{
                        '&& .MuiOutlinedInput-root:not(.Mui-disabled)': {
                          background: '#ffffff',
                        },
                        '& .MuiInputBase-input': {
                          color: '#693413',
                          '&::placeholder': { color: '#855c3d', opacity: 1 },
                          '&.Mui-disabled': { WebkitTextFillColor: '#a77b56' },
                        },
                        '& .MuiInputLabel-root': { color: '#855c3d' },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e8cdb2',
                        },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                          { borderColor: '#d4a27a' },
                        '& .MuiFormHelperText-root': { color: '#855c3d' },
                      }}
                    />
                  )}
                  {living && (
                    <TextField
                      size="small"
                      label="Name or initials (optional)"
                      value={displayLabel}
                      disabled={busy}
                      onChange={(event) => setDisplayLabel(event.target.value)}
                      inputProps={{ maxLength: 24 }}
                      helperText="Public, self-entered label. Up to 12 characters; leave blank to stay anonymous."
                      error={Array.from(displayLabel.trim()).length > 12}
                      sx={{
                        '&& .MuiOutlinedInput-root:not(.Mui-disabled)': {
                          background: 'white',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e8cdb2',
                        },
                        '& .MuiOutlinedInput-root:hover:not(.Mui-focused):not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline':
                          { borderColor: '#d4a27a' },
                        '& .MuiFormHelperText-root': { color: '#855c3d' },
                        '& .MuiInputBase-input, & .MuiInputLabel-root': {
                          color: '#855c3d',
                        },
                      }}
                    />
                  )}
                  {!poll.canAddAnswer && (
                    <Typography variant="body2" color={helperTextColor}>
                      {poll.showSubmittedAnswers
                        ? '100 different answers have been submitted. Please choose an existing answer.'
                        : 'This question has reached its answer limit. New answers cannot be added.'}
                    </Typography>
                  )}
                  <GlassButton
                    type="submit"
                    disabled={!canSubmit}
                    sx={{
                      ...answerButtonSx,
                      alignSelf: { xs: 'stretch', sm: 'flex-start' },
                      minWidth: { sm: 200 },
                    }}
                  >
                    {busy ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : poll.viewerAnswer ? (
                      'Update Answer'
                    ) : (
                      'Submit Answer'
                    )}
                  </GlassButton>
                  {poll.viewerAnswer && !poll.viewerAnswer.removed && (
                    <Button
                      color="secondary"
                      sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
                      disabled={busy}
                      onClick={() => {
                        sounds.click()
                        setEditing(false)
                        if (answerFocus) onAnswerCancel?.()
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </Stack>
              </Box>
            )}

            {closed && !living && (
              <Stack spacing={1}>
                <Typography
                  variant="h3"
                  sx={{ fontSize: 24, color: '#693413' }}
                >
                  The community has spoken
                </Typography>
                <Typography variant="body2" sx={{ color: '#855c3d', pb: 1 }}>
                  Play the top 10, or reveal the results below.
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <GlassButton
                    endIcon={<PlayArrowRoundedIcon />}
                    disabled={!poll.canPlay || busy}
                    sx={answerButtonSx}
                    onClick={() => {
                      sounds.click()
                      onPlay(poll)
                    }}
                  >
                    Play
                  </GlassButton>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<VisibilityRoundedIcon />}
                    sx={outlinedActionSx}
                    onClick={() => {
                      sounds.click()
                      setResultsOpen((value) => !value)
                    }}
                  >
                    {resultsOpen ? 'Hide Results' : 'View Results'}
                  </Button>
                </Box>
                {!poll.canPlay && (
                  <Typography variant="caption" color={helperTextColor}>
                    Needs 10 different answers to play.
                  </Typography>
                )}
                <Collapse in={resultsOpen} unmountOnExit>
                  <Stack spacing={1} pt={1}>
                    <Typography
                      variant="h3"
                      sx={{ fontSize: 24, color: '#693413' }}
                    >
                      Community Results
                    </Typography>
                    <Typography variant="caption" color={helperTextColor}>
                      Ties are ordered by which answer was submitted first.
                    </Typography>
                    {(poll.crowdRank || []).map((id, index) => {
                      const item = poll.items?.find(
                        (entry) => String(entry.id) === id,
                      )
                      const votes = poll.answerCounts?.[id] || 0
                      return (
                        <Box
                          key={id}
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                          sx={{
                            p: 1.5,
                            borderRadius: '16px',
                            background: '#fffaf5',
                            border: '1px solid #efddc9',
                          }}
                        >
                          <Box
                            sx={{
                              flexShrink: 0,
                              width: 30,
                              height: 30,
                              display: 'grid',
                              placeItems: 'center',
                              borderRadius: '10px',
                              background: '#ffdfc2',
                              color: '#693413',
                              fontWeight: 700,
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Typography
                            flex={1}
                            fontWeight={700}
                            sx={{
                              minWidth: 0,
                              overflowWrap: 'anywhere',
                              color: '#693413',
                            }}
                          >
                            {item?.title}
                          </Typography>
                          <Box textAlign="right" sx={{ flexShrink: 0 }}>
                            <Typography
                              fontWeight={700}
                              sx={{ color: '#693413' }}
                            >
                              {votes} {votes === 1 ? 'vote' : 'votes'}
                            </Typography>
                            <Typography
                              variant="caption"
                              color={helperTextColor}
                            >
                              {poll.eligibleResponses
                                ? Math.round(
                                    (votes / poll.eligibleResponses) * 100,
                                  )
                                : 0}
                              %
                            </Typography>
                          </Box>
                        </Box>
                      )
                    })}
                    {!poll.crowdRank?.length && (
                      <Typography>
                        No eligible answers were submitted.
                      </Typography>
                    )}
                  </Stack>
                </Collapse>
              </Stack>
            )}
          </Stack>
        </GlassCard>

        {living && (
          <QuestionCommunityView
            poll={poll}
            userId={userId}
            sounds={sounds}
            onUpdated={onUpdated}
            onPlay={onPlay}
            onError={(message) =>
              setAlert({ open: true, severity: 'error', message })
            }
          />
        )}

        {owner && !closed && (
          <GlassCard sx={sectionSx}>
            <Stack spacing={1.5}>
              <Typography variant="h3" sx={{ fontSize: 24, color: '#693413' }}>
                Collected Answers
              </Typography>
              <Typography variant="body2" color={helperTextColor}>
                {poll.answerCount || 0}/100 different answers. Hidden answers do
                not count toward results.
              </Typography>
              {(poll.items || []).map((item) => (
                <Box
                  key={item.id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  flexWrap="wrap"
                  sx={{
                    p: 1.5,
                    borderRadius: '16px',
                    background: '#fffaf5',
                    border: '1px solid #efddc9',
                  }}
                >
                  <Typography
                    flex={1}
                    sx={{
                      minWidth: 0,
                      overflowWrap: 'anywhere',
                      color: '#693413',
                      fontWeight: 700,
                      flexBasis: { xs: '100%', sm: 0 },
                      opacity: item.hidden ? 0.5 : 1,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {item.hidden && <Chip size="small" label="Hidden" />}
                    <Chip
                      size="small"
                      label={`${item.voteCount || 0} votes${item.hidden ? ' · excluded' : ''}`}
                      sx={{ color: '#855c3d', background: '#ffefdd' }}
                    />
                  </Stack>
                  {!item.hidden && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<VisibilityOffRoundedIcon />}
                      sx={{ ...outlinedActionSx, ml: 'auto' }}
                      disabled={busy}
                      onClick={() => {
                        sounds.click()
                        setConfirmation({ kind: 'hide', itemId: item.id })
                      }}
                    >
                      Hide
                    </Button>
                  )}
                </Box>
              ))}
              {!poll.items?.length && (
                <Typography color={helperTextColor}>
                  Share your question to collect the first answer.
                </Typography>
              )}
              {(!living || poll.canCloseAnswers) && (
                <GlassButton
                  disabled={busy}
                  sx={{
                    ...answerButtonSx,
                    alignSelf: { xs: 'stretch', sm: 'flex-start' },
                  }}
                  onClick={() => {
                    sounds.click()
                    setConfirmation({ kind: 'close' })
                  }}
                >
                  Close &amp; Show Results
                </GlassButton>
              )}
              {living && !poll.canCloseAnswers && (
                <Typography variant="caption" color="#855c3d">
                  Public questions stay open. Make this question private to
                  close submissions permanently.
                </Typography>
              )}
            </Stack>
          </GlassCard>
        )}

        {owner && (
          <GlassCard sx={sectionSx}>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="h3"
                  sx={{ fontSize: 24, color: '#693413' }}
                >
                  Discoverability
                </Typography>
                <Typography variant="body2" sx={{ color: '#855c3d', mt: 0.5 }}>
                  Choose who can find your question and see submitted answers.
                </Typography>
              </Box>
              <Autocomplete
                multiple
                freeSolo
                size="small"
                value={categories}
                options={Array.from(
                  new Set([...categoryOptions, ...categories]),
                )}
                disabled={busy}
                onChange={(_event, values) => {
                  sounds.click()
                  setCategories(normalizeMeta(values))
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Categories"
                    helperText="Up to 3 categories, 16 characters each."
                  />
                )}
              />
              <Autocomplete
                multiple
                freeSolo
                size="small"
                value={tags}
                options={Array.from(new Set([...tagOptions, ...tags]))}
                disabled={busy}
                onChange={(_event, values) => {
                  sounds.click()
                  setTags(normalizeMeta(values))
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    helperText="Up to 3 tags, 16 characters each."
                  />
                )}
              />
              <TextField
                select
                size="small"
                label="Visibility"
                value={visibility}
                disabled={busy || forcedPrivate}
                onChange={(event) => {
                  sounds.click()
                  setVisibility(event.target.value)
                }}
                helperText={
                  forcedPrivate
                    ? "This list can't be set to public."
                    : 'Private questions are shareable by link. Existing participants retain link access.'
                }
              >
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="private">Private</MenuItem>
              </TextField>
              {!closed && (
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        color="secondary"
                        checked={living ? !showAnswers : showAnswers}
                        disabled={busy}
                        onChange={(_event, value) => {
                          sounds.click()
                          setShowAnswers(living ? !value : value)
                        }}
                      />
                    }
                    label={
                      living
                        ? 'Hide answers until submissions close'
                        : 'Show submitted answers'
                    }
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontWeight: 700,
                        color: '#693413',
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color={helperTextColor}
                  >
                    {living
                      ? 'Hiding answers also prevents others from viewing the ranking or playing while submissions remain open.'
                      : 'Let participants see and choose answers others have submitted. Vote counts stay hidden.'}
                  </Typography>
                </Box>
              )}
              <Box display="flex" justifyContent="space-between" gap={1}>
                <Button
                  color="error"
                  disabled={busy}
                  onClick={() => {
                    sounds.click()
                    setConfirmation({ kind: 'delete' })
                  }}
                >
                  Delete
                </Button>
                <GlassButton
                  sx={answerButtonSx}
                  disabled={busy || !metaChanged}
                  onClick={async () => {
                    sounds.click()
                    const next = await perform(
                      () =>
                        updatePoll({
                          pollId: poll.id!,
                          category: categories.join(','),
                          tags: tags.join(','),
                          visibility,
                          ...(!closed
                            ? { showSubmittedAnswers: showAnswers }
                            : {}),
                        }),
                      'Question updated.',
                    )
                    if (next) {
                      setCategories(splitMeta(next.category))
                      setTags(splitMeta(next.tags))
                      setVisibility(
                        next.visibility === 'private' ? 'private' : 'public',
                      )
                      setShowAnswers(!!next.showSubmittedAnswers)
                    }
                  }}
                >
                  Update
                </GlassButton>
              </Box>
            </Stack>
          </GlassCard>
        )}

        <GlassCard className="rt-question-feedback" sx={sectionSx}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
          >
            <Box>
              <Typography variant="h3" sx={{ fontSize: 24, color: '#693413' }}>
                Was this fun?
              </Typography>
              <Typography variant="body2" sx={{ color: '#855c3d', mt: 0.5 }}>
                {userId
                  ? 'Help good questions rise to the top.'
                  : 'Log in to rate'}
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<ThumbUpRoundedIcon />}
                aria-pressed={rated === 1}
                sx={{
                  ...outlinedActionSx,
                  background: rated === 1 ? '#ffefdd' : '#fffaf5',
                }}
                disabled={busy || !userId}
                onClick={() => {
                  sounds.click()
                  void perform(
                    () => ratePoll({ pollId: poll.id!, rating: 1 }),
                    'Rating saved.',
                  ).then((next) => {
                    if (next && mounted.current) setRated(1)
                  })
                }}
              >
                Fun
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<ThumbDownRoundedIcon />}
                aria-pressed={rated === 0}
                sx={{
                  ...outlinedActionSx,
                  background: rated === 0 ? '#ffefdd' : '#fffaf5',
                }}
                disabled={busy || !userId}
                onClick={() => {
                  sounds.click()
                  void perform(
                    () => ratePoll({ pollId: poll.id!, rating: 0 }),
                    'Rating saved.',
                  ).then((next) => {
                    if (next && mounted.current) setRated(0)
                  })
                }}
              >
                Not Fun
              </Button>
            </Box>
          </Box>
          <Button
            size="small"
            startIcon={<FlagOutlinedIcon />}
            disabled={!userId || busy}
            sx={{
              color: '#855c3d',
              fontSize: 12,
              mt: 2,
              minHeight: 30,
              p: '4px 0',
              boxShadow: 'none',
              '&.Mui-disabled': {
                background: 'transparent',
                borderColor: 'transparent',
                boxShadow: 'none',
              },
            }}
            onClick={() => {
              sounds.click()
              setReportOpen(true)
            }}
          >
            Report
          </Button>
        </GlassCard>

        <Dialog
          open={!!confirmation}
          onClose={() => {
            if (!busy) setConfirmation(undefined)
          }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>
            {confirmation?.kind === 'close'
              ? 'Close submissions?'
              : confirmation?.kind === 'hide'
                ? 'Hide this answer?'
                : 'Delete this question?'}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {confirmation?.kind === 'close'
                ? 'This permanently stops new answers and updates, and freezes the community ranking. Participants can then play or view results.'
                : confirmation?.kind === 'hide'
                  ? 'This answer will be removed from choices and results. Its submissions will be retained privately.'
                  : 'This deletes the question and all its answers.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={busy}
              onClick={() => {
                sounds.click()
                setConfirmation(undefined)
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={busy}
              onClick={() => void confirm()}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={reportOpen}
          onClose={() => {
            if (!busy) setReportOpen(false)
          }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Report this question</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Report details"
              value={reportDetails}
              disabled={busy}
              onChange={(event) => setReportDetails(event.target.value)}
              inputProps={{ maxLength: 2000 }}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              disabled={busy}
              onClick={() => {
                sounds.click()
                setReportOpen(false)
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={busy || !reportDetails.trim()}
              onClick={async () => {
                sounds.click()
                setBusy(true)
                try {
                  const result = await reportPoll({
                    pollId: poll.id!,
                    reason: 'other',
                    details: reportDetails.trim(),
                  })
                  if (!result.success)
                    throw new Error(
                      result.message || 'Could not submit report.',
                    )
                  if (mounted.current) {
                    setReportOpen(false)
                    setReportDetails('')
                    setAlert({
                      open: true,
                      severity: 'success',
                      message: 'Report submitted.',
                    })
                  }
                } catch (error) {
                  if (mounted.current)
                    setAlert({
                      open: true,
                      severity: 'error',
                      message:
                        error instanceof Error
                          ? error.message
                          : 'Could not submit report.',
                    })
                } finally {
                  if (mounted.current) setBusy(false)
                }
              }}
            >
              Submit Report
            </Button>
          </DialogActions>
        </Dialog>
        <Alert
          alert={alert}
          onClose={() => setAlert((value) => ({ ...value, open: false }))}
        />
      </Stack>
    </PollDesign>
  )
}
